import { AddUserPage } from '@/pages/Admin/AddUserPage';
import { EditUserPage } from '@/pages/Admin/EditUserPage';
import { SettingsPage } from '@/pages/Admin/SettingsPage';
import { UserListPage } from '@/pages/Admin/UserListPage';
import { DirectoryPage } from '@/pages/Executor/DirectoryPage';
import { FileSearchPage } from '@/pages/Executor/FileSearchPage';
import { FileSearchResultPage } from '@/pages/Executor/FileSearchResultPage';
import { ReportAnalyticsPage } from '@/pages/Executor/ReportAnalyticsPage';
import { UploadFilesPage } from '@/pages/Executor/UploadFilesPage';
import { LoginPage } from '@/pages/LoginPage';
import { NoMatchPage } from '@/pages/NoMatchPage';
import { AddTagsTab } from '@/widgets/AddTagsTab';
import { AddDirectoriesTab } from '@/widgets/DirectoriesWidgets/AddDirectoriesTab';
import { AddIndexTab } from '@/widgets/IndexWidgets/AddIndexTab';
import { InputFileValidationSettings } from '@/widgets/InputFileValidationSettings';
import { LayoutWidget } from '@/widgets/LayoutWidget';
import { AddOrCopyProcessTab } from '@/widgets/ProcessWidgets/AddOrCopyProcessTab';
import { EditProcessTab } from '@/widgets/ProcessWidgets/EditProcessTab';
import { ProcessListTab } from '@/widgets/ProcessWidgets/ProcessListTab';
import { Loader } from 'lucide-react';
import { createBrowserRouter, redirect } from 'react-router';

import { LazyComponent } from '@/components/LazyComponent';

import { useAuthStore } from '@/stores/authStore';

import { USER_ROLES } from '@/constants/global-constants';
import {
  adminRoutes,
  executorRoutes,
  publicRoutes,
} from '@/constants/route-settings';

const router = createBrowserRouter([
  {
    id: 'root',
    path: publicRoutes.root,
    loader: () => {
      const { user } = useAuthStore.getState();
      return { user };
    },
    HydrateFallback: () => (
      <div className="flex h-screen items-center justify-center">
        <Loader className="animate-spin-slow" />
      </div>
    ),
    errorElement: (
      <LazyComponent>
        <NoMatchPage />
      </LazyComponent>
    ),
    children: [
      {
        path: publicRoutes.login,
        loader: async () => {
          const { user } = useAuthStore.getState();
          if (user) return redirect(adminRoutes.users);
          return null;
        },
        element: (
          <LazyComponent>
            <LoginPage />
          </LazyComponent>
        ),
      },
      {
        path: '/',
        loader: async () => {
          const { token } = useAuthStore.getState();
          if (!token) return redirect(publicRoutes.login);
          return null;
        },
        element: (
          <LazyComponent>
            <LayoutWidget />
          </LazyComponent>
        ),
        children: [
          {
            path: adminRoutes.users,
            loader: async () => {
              const { user } = useAuthStore.getState();
              if (!user || user?.role !== USER_ROLES.ADMIN.value)
                return redirect(publicRoutes.root);
              return null;
            },
            element: (
              <LazyComponent>
                <UserListPage />
              </LazyComponent>
            ),
          },
          {
            path: `${adminRoutes.settings}/*`,
            loader: async () => {
              const { user } = useAuthStore.getState();
              if (!user || user?.role !== USER_ROLES.ADMIN.value)
                return redirect(publicRoutes.root);
              return null;
            },
            element: (
              <LazyComponent>
                <SettingsPage />
              </LazyComponent>
            ),
            children: [
              {
                path: adminRoutes.fileValidation,
                index: true,
                element: (
                  <LazyComponent>
                    <InputFileValidationSettings />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.tags,
                element: (
                  <LazyComponent>
                    <AddTagsTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.indexes,
                element: (
                  <LazyComponent>
                    <AddIndexTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.processes.list,
                element: (
                  <LazyComponent>
                    <ProcessListTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.processes.add,
                element: (
                  <LazyComponent>
                    <AddOrCopyProcessTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.processes.edit,
                element: (
                  <LazyComponent>
                    <EditProcessTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.processes.copy,
                element: (
                  <LazyComponent>
                    <AddOrCopyProcessTab />
                  </LazyComponent>
                ),
              },
              {
                path: adminRoutes.directories,
                element: (
                  <LazyComponent>
                    <AddDirectoriesTab />
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: adminRoutes.addUser,
            element: (
              <LazyComponent>
                <AddUserPage />
              </LazyComponent>
            ),
          },
          {
            path: adminRoutes.editUser,
            element: (
              <LazyComponent>
                <EditUserPage />
              </LazyComponent>
            ),
          },
          // Executor routes
          {
            path: executorRoutes.myDocuments,
            element: (
              <LazyComponent>
                <DirectoryPage />
              </LazyComponent>
            ),
          },
          {
            path: `${executorRoutes.uploadFiles}/*`,
            element: (
              <LazyComponent>
                <UploadFilesPage />
              </LazyComponent>
            ),
          },
          {
            path: executorRoutes.reportAnalytics,
            element: (
              <LazyComponent>
                <ReportAnalyticsPage />
              </LazyComponent>
            ),
          },
          {
            path: executorRoutes.search,
            element: (
              <LazyComponent>
                <FileSearchPage />
              </LazyComponent>
            ),
          },
          {
            path: executorRoutes.fileSearchResult,
            element: (
              <LazyComponent>
                <FileSearchResultPage />
              </LazyComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyComponent>
        <NoMatchPage />
      </LazyComponent>
    ),
  },
]);

export default router;
